<script setup lang="ts">
  import { usePermissionsView } from '../composables/usePermissionsView'
  import AccessRolePermissionGroup, {
    type AccessRolePermissionGroupProps
  } from './lists/AccessRolePermissionGroup.vue'
  import { permissionGroups as typedPermissionGroups } from '../access-control-text'
  import { IconChevronsUp, IconChevronsDown } from '@gat-ship/icons'
  import { SearchInput, Button } from '@gat-ship/vue-components'

  // The permissions grid + search, shared by the routed AccessRolePermissionsView
  // and the AdministrationModal access-role detail modal. Reads the
  // permissions-view UI state (search, collapsed groups) from the context an
  // ancestor provides via `providePermissionsView`.
  const permissionsView = usePermissionsView()

  const permissionGroups: AccessRolePermissionGroupProps[] = Object.entries(
    typedPermissionGroups
  ).map(([groupName, groupMetadata]) => ({
    groupName,
    icon: groupMetadata.icon,
    description: groupMetadata.description ?? '',
    permissionItems: groupMetadata.permissionItems,
    searchWords: [groupName, ...(groupMetadata.description?.split(' ') ?? [])]
  }))

  /** if true, the permission groups are collapsible.
   * FG: The feature is turned off for now because it's not needed and it's a bit confusing.
   */
  const collapsiblePermissionGroups = false

  function onPermissionGroupClick(groupName: string) {
    if (!collapsiblePermissionGroups) {
      return
    }
    if (permissionsView.search.value) {
      return
    }
    permissionsView.localStorageState.toggleCollapsedGroup(groupName)
  }
</script>

<template>
  <div class="flex w-full gap-5">
    <div class="flex w-full grow flex-col gap-5">
      <div class="flex min-h-10">
        <SearchInput
          v-model="permissionsView.search.value"
          placeholder="Filter search..."
          class="w-full"
          input-class="h-10 text-sm"
          clearable
        />
      </div>
      <div
        v-if="!permissionsView.search.value && collapsiblePermissionGroups"
        class="max-w-screen flex w-full grow items-start gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <Button
          v-if="
            permissionsView.localStorageState.collapsedGroups.value.length > 0
          "
          variant="secondary"
          class="text-neutral-700"
          @click="permissionsView.localStorageState.setCollapsedGroups([])"
        >
          <icon-chevrons-up class="size-4" />
          <span> Collapse all groups </span>
        </Button>
        <Button
          v-if="
            permissionsView.localStorageState.collapsedGroups.value.length <
            permissionGroups.length
          "
          variant="secondary"
          class="text-neutral-700"
          @click="
            permissionsView.localStorageState.setCollapsedGroups(
              permissionGroups.map((group) => group.groupName)
            )
          "
        >
          <icon-chevrons-down class="size-4" />
          <span> Expand all groups </span>
        </Button>
      </div>
      <template v-if="permissionsView.search.value?.length > 0">
        <div class="flex flex-col gap-2">
          <div class="text-sm font-medium">
            Search results for "{{ permissionsView.search.value }}"
          </div>
        </div>
      </template>
      <AccessRolePermissionGroup
        v-for="permissionGroup in permissionGroups"
        :id="permissionGroup.groupName"
        :key="permissionGroup.groupName"
        :group-name="permissionGroup.groupName"
        :icon="permissionGroup.icon"
        :permission-items="permissionGroup.permissionItems"
        :description="permissionGroup.description"
        :is-collapsible="collapsiblePermissionGroups"
        @click="onPermissionGroupClick(permissionGroup.groupName)"
      />
    </div>
  </div>
</template>
